import { Box, Stack } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { IconPlus, IconUpload } from "@tabler/icons-react"
import { CountryIso3166 } from "@tfg/db"
import { ButtonWithMenu, SectionHeader } from "@tfg/ui"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { Suppliers } from "~/pages/Brand/Suppliers/SuppliersRepository/Suppliers"
import { SupplierCreationModal } from "~/pages/Brand/Suppliers/SuppliersRepository/components/SupplierCreationModal"
import { useFindContactsQuery } from "~/pages/Brand/Suppliers/SuppliersRepository/hooks/useFindContactsQuery"
import useSupplierMappingForm from "~/pages/Brand/Suppliers/SuppliersRepository/utils/useSupplierMappingForm"
import { ContactDrawer } from "~/pages/SupplierPortal/TraceabilityRequests/components/ContactDrawer"

export const SUPPLIER_INITIAL_VALUES = {
  id: "",
  supplierId: "",
  name: "",
  email: "",
  organization: {
    name: "",
    street: "",
    city: "",
    postalCode: "",
    country: CountryIso3166.FR,
    registrationId: [],
    businessRelationship: {
      id: "",
      isActive: true,
      supplierCode: "",
      type: null,
    },
  },
}

export function SuppliersSettings() {
  const { t } = useTranslation("organizationSettings")

  const [searchSupplier, setSearchSupplier] = useState<string>("")
  const [isDrawerOpen, drawerActions] = useDisclosure(false)
  const [isCreationModalOpen, creationModalActions] = useDisclosure(false)
  const [valuesUpdated, setValuesUpdated] = useState(false)
  const { supplierMapping, setSupplierMapping, handleSupplierMapping } =
    useSupplierMappingForm({})

  const suppliersQuery = useFindContactsQuery({
    searchSupplier,
  })

  const openAddSupplierModal = () => {
    creationModalActions.open()
  }

  const openAddSupplierDrawer = () => {
    setValuesUpdated(true)
  }

  const isUpdate =
    "supplierId" in supplierMapping && supplierMapping.supplierId !== ""

  useEffect(() => {
    if (valuesUpdated) {
      drawerActions.open()
      setValuesUpdated(false)
    }
  }, [valuesUpdated, drawerActions, setValuesUpdated])

  return (
    <Stack gap="md">
      <SectionHeader
        title={t("supplierSettings.title")}
        mainCta={
          <ButtonWithMenu
            items={[
              {
                label: t("supplierSettings.addSupplier"),
                icon: (
                  <Box pr="sm">
                    <IconPlus />
                  </Box>
                ),
                onClick: () => openAddSupplierModal(),
              },
              {
                icon: (
                  <Box pr="sm">
                    <IconUpload />
                  </Box>
                ),
                label: t("supplierSettings.importSuppliers"),
                onClick: () => {
                  window.open("/data-transfer/import", "_blank")
                },
              },
            ]}
          >
            {t("supplierSettings.dropdownButton.label")}
          </ButtonWithMenu>
        }
      />

      <Suppliers
        suppliersQuery={suppliersQuery}
        setInitialValues={setSupplierMapping}
        setFormType={() => undefined}
        setValuesUpdated={setValuesUpdated}
        searchSupplier={searchSupplier}
        setSearchSupplier={setSearchSupplier}
      />

      <SupplierCreationModal
        opened={isCreationModalOpen}
        onClose={creationModalActions.close}
        openDrawer={openAddSupplierDrawer}
        setInitialValues={setSupplierMapping}
        searchSupplier={searchSupplier}
      />
      <ContactDrawer
        opened={isDrawerOpen}
        setModalOpened={drawerActions}
        supplierMapping={supplierMapping}
        handleSupplierMapping={handleSupplierMapping}
        isUpdate={isUpdate}
      />
    </Stack>
  )
}
